import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { BrandLockup } from "../ui/Wordmark.js";
import { ChoiceList } from "../rfqs/steps/ChoiceList.js";
import { DescriptionStep } from "../rfqs/steps/DescriptionStep.js";
import { DescribeIssueStep } from "../rfqs/steps/DescribeIssueStep.js";
import { ConfirmRoutingStep } from "../rfqs/steps/ConfirmRoutingStep.js";
import { ImagesStep, useDraftImages } from "../rfqs/steps/ImagesStep.js";
import {
  classifyService,
  serviceCategoryLabels,
  serviceCategoryOptions,
  uploadRfqImage,
  urgencyLabels,
  urgencyOptions,
  useCreateRfq,
  type ClassifyResult,
  type ServiceCategory,
  type Urgency,
} from "../rfqs/api.js";
import { propertyTypeLabels, useCreateProperty, type PropertyType } from "../properties/api.js";
import {
  deriveNickname,
  isValidStateCode,
  usStates,
  validateAddress,
} from "../properties/address.js";
import { normalizePhone, validatePhone } from "../util/phone.js";

/**
 * Job-first intake funnel — the customer front door. Collects the repair, then
 * the property, then (only once the user is invested) personal details + an
 * account, and finally creates everything. All in-progress data lives in the
 * browser; nothing is uploaded until the account exists. See
 * `.github/intake-funnel-design.md`.
 */

type Step =
  | "category"
  | "urgency"
  | "description"
  | "photos"
  | "address"
  | "location"
  | "propertyType"
  | "name"
  | "phone"
  | "account";

const stepOrder: Step[] = [
  "category",
  "urgency",
  "description",
  "photos",
  "address",
  "location",
  "propertyType",
  "name",
  "phone",
  "account",
];

const propertyTypeOptions: PropertyType[] = [
  "single_family",
  "multi_family",
  "condo",
  "townhouse",
  "commercial",
  "other",
];

// Text answers are mirrored here so a refresh mid-funnel doesn't lose them.
// Password and photos are intentionally excluded (sensitive / in-memory only).
const DRAFT_KEY = "intake-draft-v1";

interface DraftShape {
  serviceCategory: ServiceCategory | "";
  urgency: Urgency | "";
  description: string;
  street: string;
  unit: string;
  city: string;
  stateCode: string;
  postalCode: string;
  propertyType: PropertyType;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  /** How many photos were attached — the blobs themselves aren't persisted, so
   * this lets us nudge the user to re-add them after a refresh. */
  photoCount: number;
}

function readDraft(): Partial<DraftShape> {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Partial<DraftShape>) : {};
  } catch {
    return {};
  }
}

function writeDraft(d: DraftShape): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* sessionStorage unavailable (private mode) — degrade silently. */
  }
}

function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

function validatePassword(pw: string): string | null {
  if (pw.length < 8) return "Use at least 8 characters.";
  if (!/[a-z]/.test(pw)) return "Add a lowercase letter.";
  if (!/[A-Z]/.test(pw)) return "Add an uppercase letter.";
  if (!/[0-9]/.test(pw)) return "Add a number.";
  return null;
}

function isValidEmail(e: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
}

function isUsernameExists(err: unknown): boolean {
  return err instanceof Error && err.name === "UsernameExistsException";
}

export function Intake() {
  const navigate = useNavigate();
  const { status, signUp, signIn } = useAuth();
  const createProperty = useCreateProperty();
  const createRfq = useCreateRfq();

  const saved = useMemo(() => readDraft(), []);
  // How many photos were attached before a possible refresh. Photos live only
  // in memory, so if this is non-zero on a fresh mount we prompt to re-add them.
  const restoredPhotoCount = saved.photoCount ?? 0;

  // Act 1 — the job
  const [serviceCategory, setServiceCategory] = useState<ServiceCategory | "">(
    saved.serviceCategory ?? "",
  );
  const [urgency, setUrgency] = useState<Urgency | "">(saved.urgency ?? "");
  const [description, setDescription] = useState(saved.description ?? "");
  const { images, addFiles, removeImage } = useDraftImages();

  // Act 2 — the property
  const [street, setStreet] = useState(saved.street ?? "");
  const [unit, setUnit] = useState(saved.unit ?? "");
  const [city, setCity] = useState(saved.city ?? "");
  const [stateCode, setStateCode] = useState(saved.stateCode ?? "");
  const [postalCode, setPostalCode] = useState(saved.postalCode ?? "");
  const [propertyType, setPropertyType] = useState<PropertyType>(
    saved.propertyType ?? "single_family",
  );

  // Act 3 — you + account
  const [firstName, setFirstName] = useState(saved.firstName ?? "");
  const [lastName, setLastName] = useState(saved.lastName ?? "");
  const [phone, setPhone] = useState(saved.phone ?? "");
  const [email, setEmail] = useState(saved.email ?? "");
  const [password, setPassword] = useState("");
  // "create" = new account; "signin" = email already exists, log in instead.
  const [accountMode, setAccountMode] = useState<"create" | "signin">("create");

  const [step, setStep] = useState<Step>("category");
  // AI sub-state machine living *inside* the first ("category") step — keeps the
  // funnel's step order, progress bar, and submit() untouched. Every terminal
  // path sets serviceCategory + advances to the existing next step.
  const [classifyPhase, setClassifyPhase] = useState<
    "describe" | "classifying" | "confirm" | "grid"
  >("describe");
  const [aiResult, setAiResult] = useState<ClassifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(
    null,
  );

  // Idempotency: once these exist, a retry skips re-creating them.
  const createdPropertyId = useRef<string | null>(null);
  const createdRfqId = useRef<string | null>(null);
  // True from the moment submit starts, so the signed-in redirect below doesn't
  // fire when *our own* sign-in flips auth state mid-submit.
  const submittingRef = useRef(false);

  // Persist text answers (not password/photos) for refresh resilience.
  useEffect(() => {
    writeDraft({
      serviceCategory,
      urgency,
      description,
      street,
      unit,
      city,
      stateCode,
      postalCode,
      propertyType,
      firstName,
      lastName,
      phone,
      email,
      photoCount: images.length,
    });
  }, [
    serviceCategory,
    urgency,
    description,
    street,
    unit,
    city,
    stateCode,
    postalCode,
    propertyType,
    firstName,
    lastName,
    phone,
    email,
    images.length,
  ]);

  // A returning, already-signed-in visitor doesn't belong in the funnel. Send
  // them to the app — unless we're mid-submit (our own sign-in tripped it).
  useEffect(() => {
    if (status === "signed-in" && !submittingRef.current) {
      navigate("/app", { replace: true });
    }
  }, [status, navigate]);
  if (status === "signed-in" && !submittingRef.current) return <Navigate to="/app" replace />;

  const stepIndex = stepOrder.indexOf(step);
  const goNext = () => setStep(stepOrder[stepIndex + 1] ?? step);
  const goBack = () => {
    setError(null);
    if (stepIndex > 0) setStep(stepOrder[stepIndex - 1]!);
  };

  function onAddFiles(fileList: FileList | null) {
    const errs = addFiles(fileList);
    setError(errs.length > 0 ? errs.join("; ") : null);
  }

  // Classify the description, with a hard 5s client budget so the user never
  // feels stuck. Timeout / network error / unconfigured / out-of-enum all fall
  // back to the manual category grid.
  async function runClassify() {
    const text = description.trim();
    if (text.length === 0) return;
    setError(null);
    setClassifyPhase("classifying");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await classifyService(text, controller.signal);
      clearTimeout(timeout);
      if (
        "fallback" in res ||
        !serviceCategoryOptions.includes(res.category) ||
        // The model couldn't place it (e.g. gibberish/too vague) — let the user
        // pick from the full list rather than offering "other" as a guess.
        res.category === "other"
      ) {
        setClassifyPhase("grid");
        return;
      }
      setAiResult(res);
      setClassifyPhase("confirm");
    } catch {
      clearTimeout(timeout);
      setClassifyPhase("grid");
    }
  }

  // The single handoff into the unchanged funnel: set the category, advance.
  function chooseCategory(category: ServiceCategory) {
    setServiceCategory(category);
    goNext();
  }

  // Back: within the first step's AI sub-states, step back to "describe";
  // otherwise walk the funnel back.
  const handleBack = () => {
    setError(null);
    if (step === "category" && (classifyPhase === "confirm" || classifyPhase === "grid")) {
      setClassifyPhase("describe");
      return;
    }
    goBack();
  };

  async function submit() {
    setError(null);
    setSubmitting(true);
    submittingRef.current = true;
    try {
      // 1. Account — create (auto-confirmed) then sign in, or sign in directly.
      if (accountMode === "create") {
        try {
          await signUp({
            email,
            password,
            givenName: firstName || undefined,
            familyName: lastName || undefined,
            phoneNumber: normalizePhone(phone),
          });
        } catch (err) {
          if (isUsernameExists(err)) {
            // Returning customer starting a fresh request: switch to sign-in
            // without leaving the funnel, so photos + answers survive.
            setAccountMode("signin");
            setError(
              "You already have an account. Enter your password to sign in and send this request.",
            );
            return;
          }
          throw err;
        }
      }
      await signIn({ email, password });

      // 2. Property (idempotent across retries).
      if (!createdPropertyId.current) {
        const addr = validateAddress({ street, unit, city, state: stateCode, postalCode });
        if (!addr.normalized) {
          setError("Please double-check the property address.");
          setStep("location");
          return;
        }
        const property = await createProperty.mutateAsync({
          nickname: deriveNickname(street),
          propertyType,
          ...addr.normalized,
          country: "US",
        });
        createdPropertyId.current = property.id;
      }

      // 3. RFQ (idempotent across retries).
      if (!createdRfqId.current) {
        if (!serviceCategory || !urgency || description.trim().length === 0) {
          setError("Please complete the request details.");
          setStep("category");
          return;
        }
        const rfq = await createRfq.mutateAsync({
          propertyId: createdPropertyId.current,
          serviceCategory,
          urgency,
          description: description.trim(),
          // Defer the ops email until photos attach, so it contains them.
          expectedImageCount: images.length,
        });
        createdRfqId.current = rfq.id;
      }

      // 4. Photos — non-blocking; the RFQ already exists.
      if (images.length > 0) {
        setUploadProgress({ current: 0, total: images.length });
        for (let i = 0; i < images.length; i++) {
          const img = images[i]!;
          setUploadProgress({ current: i + 1, total: images.length });
          try {
            await uploadRfqImage({
              rfqId: createdRfqId.current,
              file: img.file,
              contentType: img.contentType,
            });
          } catch (err) {
            console.error("Image upload failed", img.file.name, err);
          }
        }
      }

      clearDraft();
      navigate(`/app/verify-email?rfqId=${createdRfqId.current}`, { replace: true });
    } catch (err) {
      submittingRef.current = false;
      setError(err instanceof Error ? err.message : "Something went wrong creating your request.");
    } finally {
      setUploadProgress(null);
      setSubmitting(false);
    }
  }

  if (submitting) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-6 text-center">
        <BrandLockup className="h-auto w-[240px]" />
        <p className="mt-6 text-base font-medium text-slate-900">Sending your request…</p>
        <p className="mt-1 text-sm text-slate-500">
          {uploadProgress
            ? `Uploading photos ${uploadProgress.current}/${uploadProgress.total}…`
            : "Setting up your account."}
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-8">
      <header>
        <BrandLockup className="h-auto w-[240px]" />
        <ol className="mt-4 flex gap-1" aria-label="Progress">
          {stepOrder.map((s, i) => (
            <li
              key={s}
              className={`h-1 flex-1 rounded-full ${i <= stepIndex ? "bg-teal-700" : "bg-slate-200"}`}
            />
          ))}
        </ol>
        {(stepIndex > 0 ||
          (step === "category" && (classifyPhase === "confirm" || classifyPhase === "grid"))) && (
          <button type="button" onClick={handleBack} className="mt-3 text-sm text-teal-700">
            ‹ Back
          </button>
        )}
      </header>

      <div className="mt-6 flex-1">
        {error && (
          <div className="mb-4 rounded bg-rose-50 p-3 text-sm text-rose-900" role="alert">
            {error}
          </div>
        )}

        {step === "category" &&
          (classifyPhase === "describe" || classifyPhase === "classifying") && (
            <DescribeIssueStep
              value={description}
              onChange={setDescription}
              images={images}
              onAddFiles={onAddFiles}
              onRemoveImage={removeImage}
              onStart={runClassify}
              onChooseManually={() => setClassifyPhase("grid")}
              busy={classifyPhase === "classifying"}
            />
          )}

        {step === "category" && classifyPhase === "confirm" && aiResult && (
          <ConfirmRoutingStep
            result={aiResult}
            onConfirm={chooseCategory}
            onChooseManually={() => setClassifyPhase("grid")}
          />
        )}

        {step === "category" && classifyPhase === "grid" && (
          <ChoiceList
            heading="What do you need help with?"
            options={serviceCategoryOptions.map((c) => ({
              value: c,
              label: serviceCategoryLabels[c],
            }))}
            selected={serviceCategory}
            onSelect={(value) => chooseCategory(value as ServiceCategory)}
          />
        )}

        {step === "urgency" && (
          <ChoiceList
            heading="How soon do you need it done?"
            options={urgencyOptions.map((u) => ({ value: u, label: urgencyLabels[u] }))}
            selected={urgency}
            onSelect={(value) => {
              setUrgency(value as Urgency);
              goNext();
            }}
          />
        )}

        {step === "description" && (
          <DescriptionStep
            heading="Review your description"
            placeholder="The kitchen sink is leaking from the trap. Started yesterday."
            value={description}
            onChange={setDescription}
            onContinue={goNext}
          />
        )}

        {step === "photos" && (
          <div className="space-y-3">
            {restoredPhotoCount > 0 && images.length === 0 && (
              <p className="rounded bg-amber-50 p-3 text-sm text-amber-900">
                Your photos were cleared when the page reloaded. Please re-add them.
              </p>
            )}
            <ImagesStep
              images={images}
              onAdd={onAddFiles}
              onRemove={removeImage}
              onContinue={goNext}
            />
          </div>
        )}

        {step === "address" && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold">Where's the work needed?</h2>
            <Labeled label="Street address">
              <input
                autoFocus
                autoComplete="address-line1"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                className={inputClass}
                placeholder="123 Maple St"
              />
            </Labeled>
            <Labeled label="Unit / Apt (optional)">
              <input
                autoComplete="address-line2"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className={inputClass}
              />
            </Labeled>
            <ContinueButton disabled={street.trim().length === 0} onClick={goNext} />
          </div>
        )}

        {step === "location" && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold">City, state &amp; ZIP</h2>
            <Labeled label="City">
              <input
                autoComplete="address-level2"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={inputClass}
              />
            </Labeled>
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="State">
                <select
                  autoComplete="address-level1"
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select…</option>
                  {usStates.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code} — {s.name}
                    </option>
                  ))}
                </select>
              </Labeled>
              <Labeled label="ZIP">
                <input
                  inputMode="numeric"
                  autoComplete="postal-code"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className={inputClass}
                />
              </Labeled>
            </div>
            <ContinueButton
              disabled={
                city.trim().length === 0 ||
                !isValidStateCode(stateCode) ||
                postalCode.replace(/\D/g, "").length < 5
              }
              onClick={goNext}
            />
          </div>
        )}

        {step === "propertyType" && (
          <ChoiceList
            heading="What kind of property is it?"
            options={propertyTypeOptions.map((t) => ({ value: t, label: propertyTypeLabels[t] }))}
            selected={propertyType}
            onSelect={(value) => {
              setPropertyType(value as PropertyType);
              goNext();
            }}
          />
        )}

        {step === "name" && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold">Almost done — what's your name?</h2>
            <p className="text-sm text-slate-500">We'll use it to introduce you to pros.</p>
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="First name">
                <input
                  autoFocus
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                />
              </Labeled>
              <Labeled label="Last name (optional)">
                <input
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass}
                />
              </Labeled>
            </div>
            <ContinueButton disabled={firstName.trim().length === 0} onClick={goNext} />
          </div>
        )}

        {step === "phone" && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold">Best number to coordinate?</h2>
            <Labeled label="Phone">
              <input
                autoFocus
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(312) 555-1212"
                className={inputClass}
              />
            </Labeled>
            <p className="text-xs text-slate-500">Used only to coordinate your service requests.</p>
            <ContinueButton disabled={validatePhone(phone) !== null} onClick={goNext} />
          </div>
        )}

        {step === "account" && (
          <AccountStep
            mode={accountMode}
            email={email}
            password={password}
            onEmail={setEmail}
            onPassword={setPassword}
            onSwitchToCreate={() => {
              setAccountMode("create");
              setError(null);
            }}
            onSubmit={submit}
          />
        )}
      </div>

      {step === "category" && (
        <footer className="pt-6 text-center">
          <Link to="/sign-in" className="text-sm font-medium text-teal-700">
            I already have an account
          </Link>
        </footer>
      )}
    </main>
  );
}

const inputClass =
  "block w-full min-h-[44px] rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600";

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function ContinueButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="min-h-[44px] w-full rounded-xl bg-teal-700 text-base font-semibold text-white disabled:opacity-60"
    >
      Continue
    </button>
  );
}

function AccountStep({
  mode,
  email,
  password,
  onEmail,
  onPassword,
  onSwitchToCreate,
  onSubmit,
}: {
  mode: "create" | "signin";
  email: string;
  password: string;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onSwitchToCreate: () => void;
  onSubmit: () => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const emailOk = isValidEmail(email);
  // When signing into an existing account, don't impose new-password rules.
  const passwordError =
    mode === "create" ? validatePassword(password) : password ? null : "Enter your password.";
  const canSubmit = emailOk && passwordError === null;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
      noValidate
    >
      <div>
        <h2 className="text-base font-semibold">
          {mode === "create"
            ? "Create your account to send it"
            : "Welcome back — sign in to send it"}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Your request is ready. {mode === "create" ? "Create an account" : "Sign in"} and we'll
          send it to local pros.
        </p>
      </div>

      <Labeled label="Email">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          className={inputClass}
        />
      </Labeled>

      <Labeled label="Password">
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            autoComplete={mode === "create" ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => onPassword(e.target.value)}
            className={inputClass + " pr-16"}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-3 my-auto h-8 px-2 text-sm font-medium text-teal-700"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        {mode === "create" && (
          <p className="mt-1 text-xs text-slate-500">
            8+ characters, with upper, lower, and a digit.
          </p>
        )}
      </Labeled>

      <button
        type="submit"
        disabled={!canSubmit}
        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-teal-700 px-6 text-base font-semibold text-white shadow-sm active:bg-teal-800 disabled:opacity-60"
      >
        {mode === "create" ? "Create account & send request" : "Sign in & send request"}
      </button>

      {mode === "signin" && (
        <button
          type="button"
          onClick={onSwitchToCreate}
          className="block w-full min-h-[44px] text-sm font-medium text-slate-500"
        >
          Use a different email
        </button>
      )}
    </form>
  );
}
